import { useState } from 'react';
import {
  Package,
  Barcode,
  User,
  Building2,
  ArrowDownToLine,
  ArrowUpFromLine,
  Wrench,
  Loader2,
} from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import type { WarehouseItem } from '@/api/warehouse';
import { warehouseApi } from '@/api/warehouse';
import { MigratedCrudHistory } from '@/components/MigratedCrudHistory';
import { useQuery } from '@tanstack/react-query';
import { useReturnItem, useTakeForSelf } from '../hooks/useWarehouse';
import { TakeItemSheet } from './TakeItemSheet';

interface WarehouseItemSheetProps {
  item: WarehouseItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isMine?: boolean;
}

const FIELD_LABELS: Record<string, string> = {
  name: 'Название',
  typeId: 'Тип',
  comment: 'Комментарий',
  quantity: 'Количество',
  barcode: 'Штрихкод',
  isInOffice: 'На складе',
  assignedToName: 'Владелец',
  clientName: 'Клиент',
  isWorkUnit: 'Рабочая единица',
  updatedAt: 'Изменено',
};

export function WarehouseItemSheet({
  item,
  open,
  onOpenChange,
  isMine = false,
}: WarehouseItemSheetProps) {
  const returnItem = useReturnItem();
  const takeForSelf = useTakeForSelf();
  const [takeSheetOpen, setTakeSheetOpen] = useState(false);

  const historyQuery = useQuery({
    queryKey: ['warehouse-history', item?.id],
    queryFn: () => warehouseApi.getHistory(item!.id),
    enabled: open && !!item?.id,
  });

  // Determine item status
  const isAvailable = item?.isInOffice; // On office stock
  const holderName = item?.assignedToName || item?.legacyHolderName || 'сотрудника';
  // Can take if not mine (from office or from others)
  const canTake = !isMine;
  // Can return if it's mine and not already on office stock
  const canReturn = isMine && !isAvailable;

  const handleReturn = () => {
    if (!item?.id) return;
    if (!confirm('Вернуть товар на склад?')) return;

    returnItem.mutate(item.id, {
      onSuccess: () => {
        onOpenChange(false);
      },
    });
  };

  if (!item) return null;

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto">
          <SheetHeader className="text-left">
            <SheetTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              {item.name}
            </SheetTitle>
            {item.barcode && (
              <SheetDescription className="flex items-center gap-1">
                <Barcode className="h-3 w-3" />
                {item.barcode}
              </SheetDescription>
            )}
          </SheetHeader>

          <div className="mt-4 space-y-4">
            {/* Status */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Статус:</span>
              <Badge
                variant="outline"
                className={
                  isAvailable
                    ? 'bg-green-50 text-green-700 border-green-300'
                    : 'bg-blue-50 text-blue-700 border-blue-300'
                }
              >
                  {isAvailable ? 'На складе' : `У ${holderName}`}
              </Badge>
            </div>

            {/* Рабочая единица */}
            {item.isWorkUnit && (
              <div className="flex items-center gap-2">
                <Badge
                  variant="outline"
                  className="bg-orange-50 text-orange-700 border-orange-300"
                >
                  <Wrench className="h-3 w-3 mr-1" />
                  Рабочая единица
                </Badge>
              </div>
            )}

            {/* Comment */}
            {item.comment && (
              <div className="p-3 bg-muted rounded-lg">
                <p className="text-sm">{item.comment}</p>
              </div>
            )}

            {/* Client */}
            {item.clientName && (
              <div className="flex items-center gap-2 text-sm">
                <Building2 className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">Для клиента:</span>
                <span className="font-medium">{item.clientName}</span>
              </div>
            )}

            {/* Owner */}
            {!isAvailable && holderName && (
              <div className="flex items-center gap-2 text-sm">
                <User className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">Владелец:</span>
                <span className="font-medium">{holderName}</span>
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-col gap-2 pt-2">
              {canTake && (
                <div className="flex gap-2">
                  <Button
                    className="flex-1"
                    onClick={() => setTakeSheetOpen(true)}
                  >
                    <ArrowDownToLine className="h-4 w-4 mr-2" />
                    Для клиента
                  </Button>
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => {
                      if (!item?.id) return;
                      takeForSelf.mutate(item.id, {
                        onSuccess: () => onOpenChange(false),
                      });
                    }}
                    disabled={takeForSelf.isPending}
                  >
                    {takeForSelf.isPending ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Wrench className="h-4 w-4 mr-2" />
                    )}
                    Для себя
                  </Button>
                </div>
              )}
              {canReturn && (
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={handleReturn}
                  disabled={returnItem.isPending}
                >
                  {returnItem.isPending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <ArrowUpFromLine className="h-4 w-4 mr-2" />
                  )}
                  Вернуть на склад
                </Button>
              )}
            </div>

            <MigratedCrudHistory
              legacyLog={item.legacyLog}
              auditItems={historyQuery.data?.items || []}
              total={historyQuery.data?.total}
              isLoading={historyQuery.isLoading}
              error={historyQuery.error}
              fieldLabels={FIELD_LABELS}
            />
          </div>
        </SheetContent>
      </Sheet>

      {/* Take item sub-sheet */}
      <TakeItemSheet
        open={takeSheetOpen}
        onOpenChange={setTakeSheetOpen}
        item={item}
        onSuccess={() => {
          setTakeSheetOpen(false);
          onOpenChange(false);
        }}
      />
    </>
  );
}
